import React from 'react';

import { Icon, Text } from '@betterment/samba-react';
import { Box, Flash, Flex } from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import { AccountChecklistTask, IncompleteTask } from './AccountChecklistTask';
import { useAccount } from './AccountProvider';
import CheckingCard from './CheckingCard';
import CheckingCardActivationErrorMessage from './CheckingCardActivationErrorMessage';

import TwoCardsImage from '../../images/two_cards_right_corner.png';
import Card from '../../Shared/components/Card';
import { urlBuilder } from '../../Shared/urlBuilder';

interface Props {
  isCardVerified: boolean;
  hasCardError: boolean;
  hasExistingTransactions: boolean;
  cardExpectedDeliveryAt: string | React.ReactElement;
}

interface ChecklistContainerProps {
  displayBackgroundImage: boolean;
}

const ChecklistContainer = styled(CheckingCard)<ChecklistContainerProps>`
  background-image: url(${props => (props.displayBackgroundImage ? { TwoCardsImage } : '')});
  background-repeat: no-repeat;
  background-position: right bottom;
  @media (min-width: 700px) and (max-width: 1300px) {
    background-image: none;
  }
`;

const CardErrorFlash: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <Box m="4" pb="4">
      <Flash variant="error" icon={false}>
        <Flex m="5">
          <Box mr="3">
            <Icon name="ExclamationCircle" size="small" color="icon.critical" />
          </Box>
          <CheckingCardActivationErrorMessage />
        </Flex>
      </Flash>
    </Box>
  );
};

const AccountSetupChecklist: React.FC<React.PropsWithChildren<Props>> = ({
  isCardVerified,
  hasCardError,
  hasExistingTransactions,
  cardExpectedDeliveryAt,
}) => {
  const { account } = useAccount();

  return (
    <ChecklistContainer bg="white" displayBackgroundImage={!hasCardError}>
      <Box mb="6">
        <Card>
          <Card.Slot name="header">
            <Text
              css={{
                fontSize: 'fontSize.200',
                fontWeight: 'medium',
                margin: 'space.0',
              }}
            >
              Account setup
            </Text>
          </Card.Slot>
          <Card.Slot name="body">
            {hasCardError ? (
              <CardErrorFlash />
            ) : (
              <Flex role="list" flexDirection="column" justifyContent="space-between">
                <AccountChecklistTask iconName="checklistMarkCompleted">Order your debit card</AccountChecklistTask>
                {hasExistingTransactions ? (
                  <AccountChecklistTask iconName="checklistMarkCompleted">Fund your account</AccountChecklistTask>
                ) : (
                  <IncompleteTask
                    eventName="UserTaskFundAccount"
                    url={urlBuilder.getCheckingFundingOptions.absolute({ accountId: account.id })}
                    title="Fund your account"
                  />
                )}
                {isCardVerified ? (
                  <AccountChecklistTask iconName="checklistMarkCompleted">Activate your card</AccountChecklistTask>
                ) : (
                  <IncompleteTask
                    eventName="UserTaskActivateCard"
                    url={urlBuilder.getAppCheckingCardSetup.absolute()}
                    title="Activate your card"
                    subText={`Delivered by ${cardExpectedDeliveryAt as string}`}
                  />
                )}
              </Flex>
            )}
          </Card.Slot>
        </Card>
      </Box>
    </ChecklistContainer>
  );
};

export default AccountSetupChecklist;
